// import format from "date-fns/format";
// import { enCA, fr } from "date-fns/locale";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "../../../i18n/useLanguage";
import {
  DealerOrderPreview,
  Fulfillment,
  FullfilmentOrderLine,
  OrderFulfillment,
} from "../../../queries/usePreviewDealerOrderFromCart";
import { OrderItemTire } from "../../../stores/slices/cartSlice";

export type NormalizedDealerOrder = {
  billTo?: string;
  cost: number;
  delivery: string;
  estDeliveryDate: string;
  formattedTiresize: string;
  freight: number;
  imageLink: string;
  itemTitle: string;
  key: string;
  lineTotal: number;
  orderItem: OrderItemTire;
  quantity: number;
  source: string;
  tireLoadRating: string;
  tireManufacturerPartNumber: string;
  tirePlyRating: string;
  tireSpeedIndex: string;
};

export const useNormalizedDealerOrder = (
  data?: DealerOrderPreview
): NormalizedDealerOrder[] | null => {
  const [language] = useLanguage();
  const { t } = useTranslation();

  const normalizedData = useMemo(() => {
    if (!data?.orderFulfillmentList) {
      return [];
    }
    const orderFulfillment = data.orderFulfillmentList[0] as
      | OrderFulfillment
      | undefined;
    const inconsistentOrderLines =
      orderFulfillment?.fulfillment_value.orderLines ?? [];

    const orderLines = Array.isArray(inconsistentOrderLines)
      ? inconsistentOrderLines
      : inconsistentOrderLines.orderLine;

    // if (orderLines.length !== data.orderItemList.length) {
    //   return null;
    // }

    return data.orderItemList.map<NormalizedDealerOrder>((orderItem, i) => {
      const orderLine = orderLines[i] as FullfilmentOrderLine | undefined;
      const fulfillment = orderLine?.fulfillments.fulfillments[0] as
        | Fulfillment
        | undefined;

      const quantity = Number(orderItem.itemQty);

      const cost = parseFloat(
        orderLine?.cost ??
          orderItem.itemPart.partPricing.primaryPart.price[orderItem.itemQty]
            ?.contextList[0]?.displayPrice ??
          "0"
      );

      const lineTotal = cost * quantity;
      // const lineTotal = parseFloat(orderLine?.lineTotal ?? "0");

      const orderShippedWithOtherGoods =
        fulfillment?.shipMethod === "With Other Goods";
      return {
        billTo: orderLine?.billTo,
        cost,
        delivery: [
          orderShippedWithOtherGoods && t("dealerOrderModal.withOtherGoods"),
          String(orderFulfillment?.fulfillment_value.pickup).toLowerCase() ===
          "y"
            ? t("dealerOrderModal.pickUp")
            : t("dealerOrderModal.delivery"),
        ].find(Boolean) as string,
        estDeliveryDate: [
          orderShippedWithOtherGoods && "",
          // fulfillment.status &&
          //   format(
          //     new Date(fulfillment.estimatedDelivery),
          //     "EEE, MMM dd, yyyy h:mm aaa",
          //     { locale: language === "fr" ? fr : enCA }
          //   ),
          "N/A",
        ].find(Boolean) as string,
        formattedTiresize:
          orderItem.itemPart.partDetails.primaryPart.formattedTiresize,
        freight: parseFloat(fulfillment?.freight ?? ""),
        imageLink: orderItem.itemPart.partDetails.primaryPart.tireImageLink!,
        itemTitle: orderItem.itemTitle[language],
        key: orderItem.itemId,
        lineTotal,
        orderItem,
        quantity,
        // quantity: t("dealerOrderModal.quantity", {
        //   from: item.itemQty,
        //   to: orderLine?.quantity,
        // }),
        source: [
          Number(fulfillment?.freight) > 0 && t("dealerOrderModal.national"),
          // (item.itemHasStock || orderLine.fulfillments.fulfillments.length) &&
          //   t("dealerOrderModal.local+"),
          t("dealerOrderModal.local"),
        ].find(Boolean) as string,
        tireLoadRating:
          orderItem.itemPart.partDetails.primaryPart.tireLoadRating,
        tireManufacturerPartNumber:
          orderItem.itemPart.partDetails.primaryPart.tireManufacturerPartNumber,
        tirePlyRating: orderItem.itemPart.partDetails.primaryPart.tirePlyRating,
        tireSpeedIndex:
          orderItem.itemPart.partDetails.primaryPart.tireSpeedIndex,
      };
    });
  }, [data, language]);
  return normalizedData;
};
